import { Durations } from '@/constants/durations'
import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import { Seo } from '@/layout/Seo'
import { BlogLayout } from '@/layouts/BlogLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { BlogPost, getPosts } from '@/services/BlogService'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { getGenericStaticPaths } from '@/utils/nextUtils/staticPages'
import { getProjectTheme } from '@/utils/project-themes'
import { getBasePath } from '@/utils/sitemap/base'
import { loadTranslations } from '@/utils/translate/translate-server'
import BlogView from '@/views/BlogView'

interface BlogProps {
  locale: string
  posts: BlogPost[]
  project: {
    blogMetaDescription?: string
    name: string
  }
}

const BlogPage: NextPageWithLayout<BlogProps> = ({ locale, posts, project }) => {
  const url = `${getBasePath(locale)}${paths.blog.index}`
  const title = 'Blog | Angel'
  const description = project.blogMetaDescription ?? 'Read the latest updates from Angel'
  const openGraph = {
    title,
    description,
    url,
    type: 'website',
  }
  return (
    <>
      <Seo canonical={url} openGraph={openGraph} title={title} description={description} />
      <BlogView posts={posts} />
    </>
  )
}

BlogPage.getLayout = (page) => {
  return <BlogLayout isDarkMode={false}>{page}</BlogLayout>
}

export const getStaticProps = getStaticPropsErrorHandler<BlogProps>(
  { filePath: 'pages/[locale]/blog/index.tsx' },
  async ({ locale, next404, preview }) => {
    const { name } = getProjectTheme(slugs.angelStudios)
    const [{ project, posts }, translations] = await Promise.all([
      getPosts({ preview }),
      loadTranslations(locale, ['common', 'account']),
    ])

    if (posts?.length === 0) {
      return next404(Durations.FIVE_MINUTES_IN_SECONDS)
    }

    return {
      props: {
        locale,
        posts: posts ?? [],
        project: { blogMetaDescription: project?.blogMetaDescription, name },
        ...translations,
      },
      revalidate: Durations.FIFTEEN_MINUTES_IN_SECONDS,
    }
  },
)

export const getStaticPaths = getGenericStaticPaths

export default BlogPage
